import { useEffect, useState } from 'react';
import { Box, Link } from '@mui/material';
import { M3TitleLarge, M3BodyLarge, M3BodyMedium } from '../../reusable/TextStyles';
import { FullSizedButton } from '../../reusable/Buttons';
import { Row } from '../../reusable/Scaffolds';
import Logo from '../../../assets/Logo.svg?react';
import theme from '../../../utils/theme';
import { useServiceStatus } from '../../../hooks/useServiceStatus';
import { HEALTH_POLL_MS, retryNow } from '../../../utils/serviceStatus';

export const SUPPORT_EMAIL = 'support@quickcarehr.com';

const CLOCK_TICK_MS = 1_000;

const TIME_ONLY = new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

/** "just now", "12 s ago", "3 min ago". */
export const formatAgo = (timestampMs, nowMs) => {
  if (!timestampMs) return null;
  const seconds = Math.max(0, Math.round((nowMs - timestampMs) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds} s ago`;
  return `${Math.floor(seconds / 60)} min ago`;
};

const useNow = (intervalMs) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
};

/**
 * Shown instead of the whole app, logged in or not, only while an outage is
 * proven: the API cannot be reached, or its database cannot (see
 * utils/serviceStatus). Polls the health endpoint on its own and reloads the
 * app as soon as the outage is no longer proven.
 */
const ServiceDown = () => {
  const { since, lastCheckedAt, checking } = useServiceStatus();
  const now = useNow(CLOCK_TICK_MS);
  const checkedAgo = formatAgo(lastCheckedAt, now);

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '20px',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: theme.palette.surface.main,
      }}
    >
      <Logo style={{ width: '160px', height: 'auto' }} role="img" aria-label="QuickCare logo" />
      <M3TitleLarge>QuickCare is temporarily unavailable</M3TitleLarge>
      <M3BodyLarge style={{ color: theme.palette.onSurfaceVariant.main, maxWidth: '520px' }}>
        We can't reach the QuickCare service right now. Our team has been alerted and is working to
        restore it. This page will reconnect on its own as soon as the service is back.
      </M3BodyLarge>
      <M3BodyLarge style={{ color: theme.palette.onSurfaceVariant.main }}>
        Need help urgently? Email us at{' '}
        <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
          {SUPPORT_EMAIL}
        </Link>
      </M3BodyLarge>
      <Row style={{ gap: '12px', marginTop: '10px' }}>
        <FullSizedButton
          text={checking ? 'Checking…' : 'Try again'}
          onClick={() => { void retryNow(); }}
          type="primary"
          dense
          disabled={checking}
        />
      </Row>
      <M3BodyMedium style={{ color: theme.palette.outline.main }}>
        {since ? `Unavailable since ${TIME_ONLY.format(new Date(since))}` : 'Unavailable'}
        {checkedAgo ? ` · Last checked ${checkedAgo}` : ''}
        {` · Checking again every ${Math.round(HEALTH_POLL_MS / 1000)} seconds`}
      </M3BodyMedium>
    </Box>
  );
};

export default ServiceDown;
